import { getCookies } from "@crystal/core";
import { getBasicUser } from "../ap2/api-authn.js";
import { updateAuthnData, onAuthenticationSuccess, logoutAuthnUser } from "../ap2/state.js";
import "../api/email-detection.js";
import "@account/configs";
import "@crystal/glass-bridge";
import "@crystal/http";
import "@crystal/recaptcha";
import "@crystal/analytics";
import "react";
import { initializeAccountPortalState } from "../state/reducers.js";
import { registrationApiAction, lightRegistrationApiAction, loginApiAction, getProfileDataAction, createPasswordApiAction, updateUserAction, pageContextAction, authContextAction, runtimeInfoAction, externalAuthStartedAction, externalAuthUpdateAction, externalAuthSuccessAction, externalAuthResetAction, externalAuthErrorAction, portalOpenAction, portalCloseAction, passwordResetStartApiAction, passwordResetCompleteAction, sessionReloadApiAction, logoutApiAction } from "../state/actions.js";
import { getAccountPortalState, updateLegacyUserWithSession } from "../state/utils.js";
import { getHostInfo } from "../store/state/getters.js";
import "../store/state/host.js";
import "../store/state/modal.js";
import { getReduxStore } from "../store/state/redux-host.js";
import { useMemoryData } from "../store/storage/memory-storage.js";
import "mitt";
import { SimpleDataStore } from "../store/storage/simple-data-store.js";
import "../store/storage/universal-memory-storage.js";
import { getRuntimeInfo, getAuthentication } from "./state-getters.js";
import { hasAuthToken, isAuthNSession, AUTH_INFO_COOKIE_NAME } from "../cookie.js";
import "../external-auth/config.js";
import { retrieveExternalAuthData, removeExternalAuthData } from "../external-auth/persistence.js";
import "@crystal/config";
import { getRegistrationRedirectUrl } from "./utils.js";
import "../config/source-ids.js";
import "../config/presets.js";
import { userApi } from "../api/user.js";
const BACKUP_AUTH_INFO_COOKIE_NAME = `pending__${AUTH_INFO_COOKIE_NAME}`;
async function getUser() {
  const { user, error, authContext } = getAuthentication();
  if (authContext == null ? void 0 : authContext.isAuthHandled) {
    const { authData } = authContext;
    return (authData == null ? void 0 : authData.user) ? authData.user : Promise.reject(error);
  }
  return user ? user : Promise.reject(error);
}
const dispatch = (action) => getReduxStore().dispatch(action);
const registerUser = async (data) => {
  return dispatch(registrationApiAction(data)).then(getUser);
};
const registerLightUser = async (data) => {
  await dispatch(lightRegistrationApiAction(data));
  const { lightUser, error } = getAccountPortalState() || {};
  return lightUser ? Promise.resolve(lightUser) : Promise.reject(error);
};
const loginUser = async (credentials) => {
  return dispatch(loginApiAction({ credentials, reauthenticate: false })).unwrap().then(getUser);
};
const reAuthenticateUser = async (credentials) => {
  return dispatch(loginApiAction({ credentials, reauthenticate: true })).unwrap().then(getUser);
};
const pendingSessionReload = useMemoryData(
  "account-pending-session-reload",
  () => new SimpleDataStore()
);
const pendingLogout = useMemoryData("account-pending-logout", () => new SimpleDataStore());
const reloadSession = async ({ logoutIfUnauthenticated = true } = {}) => {
  if (pendingLogout().has()) {
    return;
  }
  if (pendingSessionReload().has()) {
    return pendingSessionReload().get();
  }
  initializeAccountPortalState();
  const doSessionReload = async () => {
    if (isLegacyConsentsRequired()) {
      requestLegacyConsents();
      return void 0;
    }
    if (!hasAuthToken()) {
      const { isAuthenticated } = getAuthentication();
      if (isAuthenticated && logoutIfUnauthenticated) {
        logoutUser();
      }
      return;
    }
    if (isLegacySocialLogin()) {
      await reloadSessionLegacyExtAuth();
    } else {
      await updateSessionData(logoutIfUnauthenticated);
    }
    return getUser();
  };
  const promise = doSessionReload().finally(() => {
    pendingSessionReload().remove();
  });
  pendingSessionReload().set(promise);
  return promise;
};
const updateSessionData = async (logoutIfUnauthenticated) => {
  if (isAuthNSession()) {
    const isSessionReload = !isRedirectedPath();
    await getBasicUser().then((u) => {
      const user = updateLegacyUserWithSession({
        ...u,
        session: {
          ...u.session,
          isSessionReload
        }
      });
      if (isSessionReload) {
        updateAuthnData(user);
      } else {
        window.sessionStorage.removeItem("ap-redirected");
        onAuthenticationSuccess(user, false);
      }
    }).catch(() => {
      if (logoutIfUnauthenticated) {
        logoutUser();
      }
    });
  } else {
    await dispatch(sessionReloadApiAction({ logoutIfUnauthenticated })).catch(() => void 0);
  }
};
function isLegacySocialLogin() {
  return !!retrieveExternalAuthData();
}
function isRedirectedPath() {
  return typeof (window == null ? void 0 : window.sessionStorage) !== "undefined" && !!window.sessionStorage.getItem("ap-redirected");
}
const reloadSessionLegacyExtAuth = async () => {
  const externalAuthData = retrieveExternalAuthData();
  userApi.reloadSession().then((u) => {
    const user = updateLegacyUserWithSession(u);
    setExternalAuthSuccess({
      ...externalAuthData,
      user,
      isAuthenticated: true,
      isPersistent: user.session.isPersistent
    });
    removeExternalAuthData();
  }).catch(() => void 0);
};
function isLegacyConsentsRequired() {
  return !!getCookies()[BACKUP_AUTH_INFO_COOKIE_NAME];
}
function requestLegacyConsents() {
  const { authContext } = getAuthentication();
  setAuthContext({
    ...authContext,
    shouldUpdateConsents: true
  });
}
const getProfileData = () => dispatch(getProfileDataAction());
const logoutUser = async () => {
  var _a;
  if (pendingLogout().has()) {
    return;
  }
  pendingLogout().set("pending-logout");
  const doLogout = async () => {
    if (isAuthNSession()) {
      await logoutAuthnUser();
    } else {
      await dispatch(logoutApiAction());
    }
  };
  if (pendingSessionReload().has()) {
    await ((_a = pendingSessionReload().get()) == null ? void 0 : _a.catch());
  }
  return doLogout().finally(() => {
    pendingLogout().remove();
  });
};
const createUserPassword = async (data) => {
  return dispatch(createPasswordApiAction(data)).then(getUser);
};
const updateUser = (user) => {
  dispatch(updateUserAction(user));
};
const setPageContext = (pageContext) => {
  dispatch(pageContextAction(pageContext));
};
const setAuthContext = (payload) => {
  dispatch(authContextAction(payload));
};
const updateRuntimeInfo = (runtimeInfo) => {
  dispatch(runtimeInfoAction(runtimeInfo));
};
const setExternalAuthStart = (payload) => {
  dispatch(externalAuthStartedAction(payload));
};
const setExternalAuthData = (payload) => {
  dispatch(externalAuthUpdateAction(payload));
};
const setExternalAuthSuccess = (payload) => {
  dispatch(externalAuthSuccessAction(payload));
};
const resetExternalAuthState = () => {
  dispatch(externalAuthResetAction());
};
const setExternalAuthError = (payload) => {
  dispatch(externalAuthErrorAction(payload));
};
const openAccountPortal = (runtimeInfo) => {
  const info = runtimeInfo;
  info.preset = info.preset || "modal";
  updateRuntimeInfo({
    ...info,
    hintAcid: info.hintAcid || void 0,
    propSourceId: info.sourceId
  });
  if (getRuntimeInfo().hasModalClient) {
    dispatch(portalOpenAction());
  } else if (typeof window !== "undefined") {
    const registrationUrl = getRegistrationRedirectUrl(getHostInfo().context, info.touchpoint);
    window.location.assign(registrationUrl);
  }
};
const closeAccountPortal = () => {
  if (getRuntimeInfo().isModalVisible) {
    dispatch(portalCloseAction());
  }
};
const ensureAuthenticated = () => {
  var _a, _b;
  const MINUTE = 60 * 1e3;
  const AUTOMATION_REDUCE_TIMESTAMP = 60 * MINUTE + 58 * MINUTE;
  const PERSISTENT_LOGIN_AUTOMATION_COOKIE = "PERSISTENT_LOGIN_AUTOMATION_ENABLED";
  const isAutomationEnabled = getCookies()[PERSISTENT_LOGIN_AUTOMATION_COOKIE] === "true";
  const { user } = getAuthentication();
  if (!user) {
    return false;
  }
  const expirationTime = isAutomationEnabled ? ((_a = user.session) == null ? void 0 : _a.reAuthAfter) - AUTOMATION_REDUCE_TIMESTAMP : (_b = user.session) == null ? void 0 : _b.reAuthAfter;
  const currentTimestamp = Date.now();
  const shouldOpenReauthModal = currentTimestamp > expirationTime - MINUTE / 2;
  if (shouldOpenReauthModal) {
    openAccountPortal({ preset: "re-authentication", hintAcid: user.acid, pageContext: {} });
    return false;
  } else {
    return true;
  }
};
const startPasswordReset = async (data) => {
  await dispatch(passwordResetStartApiAction(data));
  const { eventType, status, error } = getAccountPortalState() || {};
  if (eventType === "passwordResetStart" && status === "error") {
    return Promise.reject(error);
  }
};
const completePasswordReset = async (data) => {
  dispatch(passwordResetCompleteAction(data));
};
export {
  closeAccountPortal,
  completePasswordReset,
  createUserPassword,
  ensureAuthenticated,
  getProfileData,
  loginUser,
  logoutUser,
  openAccountPortal,
  reAuthenticateUser,
  registerLightUser,
  registerUser,
  reloadSession,
  resetExternalAuthState,
  setAuthContext,
  setExternalAuthData,
  setExternalAuthError,
  setExternalAuthStart,
  setExternalAuthSuccess,
  setPageContext,
  startPasswordReset,
  updateRuntimeInfo,
  updateUser
};
//# sourceMappingURL=state-triggers.js.map
